import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { DataTableColumn, SortState } from '../components/DataTable';

/**
 * Per-table column visibility for `DataTable`.
 *
 * It returns the column array ALREADY FILTERED, so `DataTable` keeps mapping
 * whatever it is handed and its render body never learns about visibility.
 * Its header, body and skeleton loops all read the same `columns` prop, so
 * filtering here drops a hidden column from all three at once — including the
 * loading shape.
 *
 * Hiding is opt-in per column (`hideable`). A column that does not declare it
 * is never offered and can never be hidden, which is how the identity and
 * actions columns stay on screen by construction rather than by a flag someone
 * has to remember to set.
 *
 * The stored preference deliberately SURVIVES an organization switch. That
 * diverges from `useInfiniteList`, which restores search/sort/filters on `resetKey`, and
 * the divergence is the rule: shape preferences persist, query state does not.
 */

/**
 * Every column array this hook has handed out. DataTable asks
 * `isVisibilityManaged(columns)` in development and logs an error when a
 * column declares `hideable` / `defaultHidden` / `hideBelow` but the array did
 * not come from here — those flags do nothing on their own (DESIGN_SYSTEM.md §9).
 */
const MANAGED = new WeakSet<object>();

export function isVisibilityManaged(columns: object): boolean {
  return MANAGED.has(columns);
}

const COLUMN_STORAGE_PREFIX = 'dory.portal.columns.';
const STORAGE_VERSION = 1;

/** Tailwind's `sm` and `md`, phrased as "is the viewport narrow?". */
const BREAKPOINT_QUERY: Record<NonNullable<DataTableColumn<unknown>['hideBelow']>, string> = {
  sm: '(max-width: 639.98px)',
  md: '(max-width: 767.98px)',
};

/**
 * Passed at call time so hiding the sorted column repairs the sort in the SAME
 * handler. An effect would run one render later, and because the sort handler
 * also resets to page 1, the user's page would silently jump as a side effect
 * of hiding a column.
 */
export interface ColumnSortGuard {
  activeSort: SortState | undefined;
  onSortChange: (sort: SortState) => void;
}

export interface ColumnVisibilityState<T> {
  /** Hand straight to DataTable: preference and breakpoint already applied. */
  columns: Array<DataTableColumn<T>>;
  hiddenKeys: ReadonlySet<string>;
  /**
   * Columns the CURRENT BREAKPOINT removes, whatever the stored preference
   * says. `ColumnPicker` omits these: a menu headed "Visible columns" must not
   * show a tick beside a column that is provably not in the table, and a tap
   * on one would reshape the desktop layout with no on-screen feedback.
   */
  breakpointHiddenKeys: ReadonlySet<string>;
  /**
   * Columns the page removes at a measured container width
   * (`containerHiddenKeys`, e.g. Sensors beside its map). Same contract as
   * breakpoint hiding: out of the picker, out of `hiddenCount`, never stored,
   * and the user's sort is left as they set it.
   */
  containerHiddenKeys: ReadonlySet<string>;
  /** Breakpoint ∪ container: every column the LAYOUT removes. `ColumnPicker` omits these. */
  layoutHiddenKeys: ReadonlySet<string>;
  /** Drives the trigger's "Columns, N hidden". Excludes layout hiding. */
  hiddenCount: number;
  isDefault: boolean;
  toggle: (key: string, guard?: ColumnSortGuard) => void;
  reset: (guard?: ColumnSortGuard) => void;
  /**
   * `defaultSort`, unless the RESTORED preference hides that column — then the
   * first visible sortable column. Pass to `useInfiniteList` as `initialSort`;
   * it is resolved before the first render, so the table never paints sorted
   * by a header that is not in the DOM.
   */
  initialSort: SortState;
}

const storageKeyFor = (tableId: string): string => `${COLUMN_STORAGE_PREFIX}${tableId}`;

/** Validate-on-read with a working fallback — the ThemeContext precedent. */
const readStoredHidden = (tableId: string): string[] | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(storageKeyFor(tableId));
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const record = parsed as { version?: unknown; hidden?: unknown };
    if (record.version !== STORAGE_VERSION) return null;
    if (!Array.isArray(record.hidden)) return null;
    if (!record.hidden.every((key) => typeof key === 'string')) return null;
    return record.hidden as string[];
  } catch (error) {
    console.error('Failed to read column preferences from storage:', error);
    return null;
  }
};

const writeStoredHidden = (tableId: string, hidden: ReadonlySet<string>): void => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      storageKeyFor(tableId),
      JSON.stringify({ version: STORAGE_VERSION, hidden: Array.from(hidden).sort() }),
    );
  } catch (error) {
    console.error('Failed to save column preferences to storage:', error);
  }
};

const clearStoredHidden = (tableId: string): void => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(storageKeyFor(tableId));
  } catch (error) {
    console.error('Failed to clear column preferences from storage:', error);
  }
};

/**
 * `false` whenever matchMedia is unavailable, so an environment without it —
 * jsdom, SSR — shows every column rather than silently hiding the responsive
 * ones.
 */
const matchesQuery = (query: string): boolean => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  try {
    return window.matchMedia(query).matches;
  } catch {
    return false;
  }
};

const defaultHiddenKeys = <T,>(columns: Array<DataTableColumn<T>>): Set<string> =>
  new Set(columns.filter((column) => column.hideable && column.defaultHidden).map((c) => c.key));

const sameKeys = (a: ReadonlySet<string>, b: ReadonlySet<string>): boolean =>
  a.size === b.size && Array.from(a).every((key) => b.has(key));

const NO_KEYS: ReadonlySet<string> = new Set();

export function useColumnVisibility<T>({
  tableId,
  columns,
  defaultSort,
  containerHiddenKeys = NO_KEYS,
}: {
  tableId: string;
  columns: Array<DataTableColumn<T>>;
  defaultSort: SortState;
  /**
   * Columns the page drops because its measured container is too narrow.
   * Pass a stable Set (module-level constant, or undefined when nothing is
   * dropped). Treated exactly like `hideBelow`: never offered, counted or
   * stored, and the sort is not rewritten — a sort change reloads the list
   * from the top, and opening a side panel must not do that.
   */
  containerHiddenKeys?: ReadonlySet<string>;
}): ColumnVisibilityState<T> {
  // Columns are rebuilt inline on most pages, so keep the latest in a ref and
  // out of every callback's dependency list.
  const columnsRef = useRef(columns);
  columnsRef.current = columns;
  const defaultSortRef = useRef(defaultSort);
  defaultSortRef.current = defaultSort;

  const [hiddenKeys, setHiddenKeys] = useState<ReadonlySet<string>>(() => {
    const hideable = new Set(columns.filter((c) => c.hideable).map((c) => c.key));
    const stored = readStoredHidden(tableId);
    // A stored key naming a column that no longer exists is inert, and the next
    // write prunes it. A column added in a later release is absent from the
    // stored array, so it appears at its own default — no migration step.
    if (stored) return new Set(stored.filter((key) => hideable.has(key)));
    return defaultHiddenKeys(columns);
  });

  const [narrow, setNarrow] = useState<Record<'sm' | 'md', boolean>>(() => ({
    sm: matchesQuery(BREAKPOINT_QUERY.sm),
    md: matchesQuery(BREAKPOINT_QUERY.md),
  }));

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const entries = (['sm', 'md'] as const).map((tier) => {
      const media = window.matchMedia(BREAKPOINT_QUERY[tier]);
      const onChange = () => setNarrow((prev) => ({ ...prev, [tier]: media.matches }));
      // Safari <14 only has the deprecated pair; ThemeContext handles the same.
      if (typeof media.addEventListener === 'function') media.addEventListener('change', onChange);
      else media.addListener(onChange);
      return { media, onChange };
    });
    setNarrow({ sm: matchesQuery(BREAKPOINT_QUERY.sm), md: matchesQuery(BREAKPOINT_QUERY.md) });
    return () => {
      for (const { media, onChange } of entries) {
        if (typeof media.removeEventListener === 'function') {
          media.removeEventListener('change', onChange);
        } else {
          media.removeListener(onChange);
        }
      }
    };
  }, []);

  /**
   * Sort reconciliation considers only the USER preference, never the
   * breakpoint: rewriting someone's sort because they rotated their phone
   * would be a worse surprise than a sort with no visible header.
   */
  const resolveSortFor = useCallback((hidden: ReadonlySet<string>): SortState => {
    const all = columnsRef.current;
    const fallbackSort = defaultSortRef.current;
    const visible = (column: DataTableColumn<T>) => !(column.hideable && hidden.has(column.key));

    const defaultColumn = all.find((column) => column.key === fallbackSort.column);
    if (defaultColumn && visible(defaultColumn)) return fallbackSort;

    const firstSortable = all.find((column) => column.sortable && visible(column));
    return firstSortable
      ? { column: firstSortable.key, direction: fallbackSort.direction }
      : fallbackSort;
  }, []);

  // Resolved once, before the first render, because useInfiniteList reads
  // initialSort only on mount.
  const initialSortRef = useRef<SortState | null>(null);
  if (initialSortRef.current === null) {
    initialSortRef.current = resolveSortFor(hiddenKeys);
  }

  const toggle = useCallback(
    (key: string, guard?: ColumnSortGuard) => {
      const column = columnsRef.current.find((candidate) => candidate.key === key);
      if (!column?.hideable) return;

      const next = new Set(hiddenKeys);
      const willHide = !next.has(key);
      if (willHide) next.add(key);
      else next.delete(key);

      setHiddenKeys(next);
      writeStoredHidden(tableId, next);

      if (willHide && guard && guard.activeSort?.column === key) {
        guard.onSortChange(resolveSortFor(next));
      }
    },
    [hiddenKeys, tableId, resolveSortFor],
  );

  const reset = useCallback(
    (guard?: ColumnSortGuard) => {
      const next = defaultHiddenKeys(columnsRef.current);
      setHiddenKeys(next);
      clearStoredHidden(tableId);

      const active = guard?.activeSort;
      if (guard && active && next.has(active.column)) {
        guard.onSortChange(resolveSortFor(next));
      }
    },
    [tableId, resolveSortFor],
  );

  const visibleColumns = useMemo(() => {
    const visible = columns.filter((column) => {
      if (column.hideable && hiddenKeys.has(column.key)) return false;
      if (column.hideBelow && narrow[column.hideBelow]) return false;
      if (containerHiddenKeys.has(column.key)) return false;
      return true;
    });
    MANAGED.add(visible);
    return visible;
  }, [columns, hiddenKeys, narrow, containerHiddenKeys]);

  const breakpointHiddenKeys = useMemo(
    () =>
      new Set(
        columns
          .filter((column) => column.hideBelow && narrow[column.hideBelow])
          .map((column) => column.key),
      ),
    [columns, narrow],
  );

  const layoutHiddenKeys = useMemo(
    () => new Set([...Array.from(breakpointHiddenKeys), ...Array.from(containerHiddenKeys)]),
    [breakpointHiddenKeys, containerHiddenKeys],
  );

  const isDefault = useMemo(
    () => sameKeys(hiddenKeys, defaultHiddenKeys(columns)),
    [hiddenKeys, columns],
  );

  return {
    columns: visibleColumns,
    hiddenKeys,
    breakpointHiddenKeys,
    containerHiddenKeys,
    layoutHiddenKeys,
    hiddenCount: hiddenKeys.size,
    isDefault,
    toggle,
    reset,
    initialSort: initialSortRef.current,
  };
}
